// src/components/NotificationDropdown.jsx
import { Fragment, useMemo, useState } from 'react'
import { Popover, Transition } from '@headlessui/react'
import { BellIcon, InboxIcon, CheckIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useNotifications } from '@/context/NotificationContext'
import NotificationItem from '@/components/NotificationItem'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

export default function NotificationDropdown({ align = 'right', tone = 'light' }) {
  const {
    notifications,
    loadingNotifications,
    markAllNotificationsAsRead,
    archiveNotification
  } = useNotifications();

  // Panel anchoring. Default 'right' (mobile top bar — opens leftward
  // from a top-right bell). 'left' is for the desktop/tablet left
  // sidebar so the panel opens rightward into the content area instead
  // of off the left screen edge.
  const panelAlign = align === 'left'
    ? 'left-0 origin-top-left'
    : 'right-0 origin-top-right';

  const [isClearing, setIsClearing] = useState(false);
  // NEW: Local state to hide items instantly while backend updates
  const [clearedIds, setClearedIds] = useState(new Set());

  // 1. UI Safety Filter
  const visibleNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    
    return notifications.filter(n => {
      // Filter out standard removed items
      if (n.deleted || n.isArchived || n.dismissed || n.archived) return false;
      
      // Filter out items we just cleared locally (Optimistic UI)
      if (clearedIds.has(n.id)) return false;

      // Filter out Chat messages
      const type = n.type || n.data?.type || '';
      const isChat = ['message', 'chat', 'new_chat_message'].includes(type);
      if (isChat) return false; 
      
      return true;
    });
  }, [notifications, clearedIds]);

  // 2. Counts
  const unreadCount = useMemo(() => {
    return visibleNotifications.reduce((acc, n) => (n.isRead === false ? acc + 1 : acc), 0);
  }, [visibleNotifications]);

  const hasItems = visibleNotifications.length > 0;

  // 3. Robust "Clear All" Handler
  const handleClearAll = async () => {
    if (!hasItems) return;
    setIsClearing(true);

    // A. Capture IDs to clear
    const idsToArchive = visibleNotifications.map(n => n.id);

    // B. OPTIMISTIC UPDATE: Hide them immediately
    setClearedIds(prev => {
      const next = new Set(prev);
      idsToArchive.forEach(id => next.add(id));
      return next;
    });

    // C. Perform Backend Operation
    try {
      if (archiveNotification) {
        // Run all archive requests in parallel
        await Promise.all(idsToArchive.map(id => archiveNotification(id)));
      } else {
        console.warn("archiveNotification function missing from Context");
      }
    } catch (error) {
      console.error("Failed to clear notifications", error);
      // Optional: If it fails, you could revert the optimistic update here
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Popover className="relative">
      <Popover.Button className={`relative focus:outline-none flex items-center justify-center h-11 w-11 rounded-full transition-all active:scale-95 ${
        tone === 'dark' ? 'hover:bg-white/10' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'
      }`}>
        <BellIcon className={`w-6 h-6 ${tone === 'dark' ? 'text-white/80' : 'text-zinc-600 dark:text-zinc-300'}`} />
        
        {/* Red Dot only shows if there are UNREAD items */}
        {unreadCount > 0 && (
          <>
            <span className="absolute top-2.5 right-2.5 inline-flex h-2.5 w-2.5 rounded-full bg-red-500 animate-ping opacity-75" />
            <span className="absolute top-2.5 right-2.5 inline-flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-black" />
          </>
        )}
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-2 scale-95"
        enterTo="opacity-100 translate-y-0 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0 scale-100"
        leaveTo="opacity-0 translate-y-2 scale-95"
      >
        <Popover.Panel className={`absolute z-50 mt-3 ${panelAlign} w-80 max-w-[calc(100vw-1rem)] transform rounded-2xl bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 focus:outline-none`}>
          
          {/* Header */}
          <div className="flex justify-between items-center px-4 py-3 border-b border-zinc-100 dark:border-zinc-800/50">
            <h3
              className="text-sm font-bold uppercase tracking-wide text-[#0d1f14] dark:text-zinc-100"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              Notifications
            </h3>

            <div className="flex items-center gap-1">
              {/* 'Mark Read' only shows if you have unread dots */}
              {unreadCount > 0 && (
                <button
                  onClick={markAllNotificationsAsRead}
                  className="group flex items-center gap-1 text-xs font-semibold text-[#5a8a1f] hover:text-[#3d6010] py-1.5 px-2 rounded-md hover:bg-[#b5f03d]/15 transition-colors"
                >
                  <CheckIcon className="w-3 h-3" />
                  <span>Mark read</span>
                </button>
              )}

              {/* 'Clear' shows if list has items */}
              {hasItems && (
                <button 
                  onClick={handleClearAll}
                  disabled={isClearing}
                  className="group flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 py-1.5 px-2 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <TrashIcon className="w-3 h-3" />
                  <span>{isClearing ? 'Clearing...' : 'Clear'}</span>
                </button>
              )}
            </div>
          </div>
          
          {/* List Area */}
          <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-200 dark:scrollbar-thumb-zinc-700 scrollbar-track-transparent">
            {loadingNotifications ? (
              <div className="py-8 flex justify-center">
                 <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-zinc-500"></div>
              </div>
            ) : !hasItems ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="h-12 w-12 rounded-full bg-zinc-50 dark:bg-zinc-800/50 flex items-center justify-center mb-3">
                  <InboxIcon className="h-6 w-6 text-zinc-300 dark:text-zinc-600" />
                </div>
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-200">All caught up</p>
                <p className="text-xs text-zinc-500 mt-1">No new system alerts at the moment.</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100 dark:divide-zinc-800/50">
                {visibleNotifications.map(notif => (
                  <NotificationItem key={notif.id} notification={notif} />
                ))}
              </div>
            )}
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  )
}